import { useMutation } from "@apollo/client";
import { InputControl, SelectControl } from "@aurem/components";
import { Button, Text, Toast } from "@aurem/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { Play } from "lucide-react";
import { useConfigWorkflows } from "pages/Controls/Workflow/New/hooks/use-workflow.hooks";
import { useCallback, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  FeeWorkflowKey,
  gql,
  TriggerType,
  WorkflowKey,
  WorkflowType,
} from "types";
import { idSchema } from "utils/id-schema";
import { z } from "zod";
import { DevToolbarCard } from "../components/DevToolbarCard";

const PROCESS_EMPLOYEE_TERMINATIONS = gql(/* GraphQL */ `
  mutation DevToolbarProcessEmployeeTerminations {
    processEmployeeTerminations
  }
`);

export const TRIGGER_WORKFLOW = gql(/* GraphQL */ `
  mutation TriggerWorkflow($workflowKey: WorkflowKey!, $context: JSON) {
    runWorkflow(workflowKey: $workflowKey, context: $context) {
      id
    }
  }
`);

const TRIGGER_FEE_WORKFLOW = gql(/* GraphQL */ `
  mutation DevToolbarTriggerFeeWorkflow($workflowKey: FeeWorkflowKey!) {
    triggerFeeWorkflow(workflowKey: $workflowKey)
  }
`);

const workflowSchema = z.object({
  productInstanceId: idSchema(),
  workflowKey: z.string(),
});

type WorkflowFormData = z.infer<typeof workflowSchema>;

const feeWorkflowSchema = z.object({
  workflowKey: z.string(),
});

type FeeWorkflowFormData = z.infer<typeof feeWorkflowSchema>;

const ProcessTerminatedEmployeesCard = () => {
  const [processTerminations, { loading }] = useMutation(
    PROCESS_EMPLOYEE_TERMINATIONS,
  );

  const handleClick = useCallback(async () => {
    try {
      await processTerminations();
      Toast.success({
        title: "Terminated employees processing started",
      });
    } catch (error) {
      Toast.error({
        title: "Failed to process terminated employees",
        text: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }, [processTerminations]);

  return (
    <DevToolbarCard
      title="Process Terminated Employees"
      testId="devtoolbar-process-terminated-employees"
    >
      <div className="flex flex-col gap-2">
        <Text size="xs" className="text-gray500">
          Run the daily termination job for employees whose end date is today or
          earlier.
        </Text>
        <div className="flex justify-end">
          <Button
            size="xs"
            loading={loading}
            onClick={handleClick}
            startIcon={<Play size={12} />}
            data-e2e-id="devtoolbar-process-terminated-employees-run"
          >
            Run
          </Button>
        </div>
      </div>
    </DevToolbarCard>
  );
};

const TriggerWorkflowCard = () => {
  const [triggerWorkflow, { loading }] = useMutation(TRIGGER_WORKFLOW);

  const { configWorkflows } = useConfigWorkflows();
  const workflowKeyOptions = useMemo(
    () =>
      configWorkflows
        .filter(
          (wf) =>
            wf.workflowType === WorkflowType.Product &&
            wf.trigger.type !== TriggerType.Hook,
        )
        .map((wf) => ({ id: wf.key, text: wf.name })),
    [configWorkflows],
  );

  const form = useForm<WorkflowFormData>({
    resolver: zodResolver(workflowSchema),
    defaultValues: {
      productInstanceId: "",
      workflowKey: "",
    },
  });

  const { handleSubmit, reset } = form;

  const onSubmit = useCallback(
    async (input: WorkflowFormData) => {
      try {
        const workflowKey = (input.workflowKey ||
          WorkflowKey.PayrollDeductionForSavings) as WorkflowKey;

        await triggerWorkflow({
          variables: {
            workflowKey,
            context: { productInstanceId: input.productInstanceId },
          },
        });
        Toast.success({ title: "Workflow run successfully" });
        reset();
      } catch (error) {
        Toast.error({
          title: "Failed to run workflow",
          text: error instanceof Error ? error.message : "Unknown error",
        });
      }
    },
    [triggerWorkflow, reset],
  );

  return (
    <DevToolbarCard
      title="Run Product Workflow"
      testId="devtoolbar-run-workflow"
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className="flex flex-col gap-2"
        >
          <InputControl
            name="productInstanceId"
            size="sm"
            placeholder="Product Instance ID"
            data-e2e-id="devtoolbar-run-workflow-product-instance-id"
          />
          <SelectControl
            name="workflowKey"
            size="sm"
            options={workflowKeyOptions}
            placeholder="Workflow config (Payroll if empty)"
            data-e2e-id="devtoolbar-run-workflow-workflow-key"
          />
          <div className="flex justify-end">
            <Button
              type="submit"
              size="xs"
              loading={loading}
              startIcon={<Play size={12} />}
              data-e2e-id="devtoolbar-run-workflow-run"
            >
              Run
            </Button>
          </div>
        </form>
      </FormProvider>
    </DevToolbarCard>
  );
};

const TriggerFeeWorkflowCard = () => {
  const [triggerFeeWorkflow, { loading }] = useMutation(TRIGGER_FEE_WORKFLOW);

  const { configWorkflows } = useConfigWorkflows();
  const feeWorkflowKeyOptions = useMemo(
    () =>
      configWorkflows
        .filter((wf) => wf.workflowType === WorkflowType.Fee)
        .map((wf) => ({
          id: wf.key,
          text: wf.name,
          dataAttributes: {
            "data-e2e-id": `devtoolbar-trigger-fee-workflow-key-option-${wf.key}`,
          },
        })),
    [configWorkflows],
  );

  const form = useForm<FeeWorkflowFormData>({
    resolver: zodResolver(feeWorkflowSchema),
    defaultValues: { workflowKey: "" },
  });

  const { handleSubmit, reset } = form;

  const onSubmit = useCallback(
    async (input: FeeWorkflowFormData) => {
      try {
        if (!input.workflowKey) {
          Toast.error({ title: "Select a fee workflow" });
          return;
        }

        const result = await triggerFeeWorkflow({
          variables: { workflowKey: input.workflowKey as FeeWorkflowKey },
        });
        const runIds = result.data?.triggerFeeWorkflow ?? [];

        Toast.success({
          title: `Fee workflow triggered (${runIds.length} run${runIds.length !== 1 ? "s" : ""})`,
        });
        reset();
      } catch (error) {
        Toast.error({
          title: "Failed to trigger fee workflow",
          text: error instanceof Error ? error.message : "Unknown error",
        });
      }
    },
    [triggerFeeWorkflow, reset],
  );

  return (
    <DevToolbarCard
      title="Run Fee Workflow"
      testId="devtoolbar-trigger-fee-workflow"
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className="flex flex-col gap-2"
        >
          <SelectControl
            name="workflowKey"
            size="sm"
            options={feeWorkflowKeyOptions}
            placeholder="Fee workflow"
            data-e2e-id="devtoolbar-trigger-fee-workflow-key"
          />
          <div className="flex justify-end">
            <Button
              type="submit"
              size="xs"
              loading={loading}
              startIcon={<Play size={12} />}
              data-e2e-id="devtoolbar-trigger-fee-workflow-run"
            >
              Run
            </Button>
          </div>
        </form>
      </FormProvider>
    </DevToolbarCard>
  );
};

export const TemporalTab = () => {
  return (
    <div
      className="px-4 py-3 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3"
      data-e2e-id="devtoolbar-temporal-tab"
    >
      <TriggerWorkflowCard />
      <TriggerFeeWorkflowCard />
      <ProcessTerminatedEmployeesCard />
    </div>
  );
};
